import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    AccessTeamUserDTO,
    useDeleteAccessTeamUserApi,
    useSaveAccessTeamUserApi,
    useSearchAccessTeamUserApi,
} from '../../../api/access-team-user-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveAccessTeamUserModal } from './save-access-team-user-modal';

export function AccessTeamUserApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedAccessTeamUser, setSelectedAccessTeamUser] = useState<
        AccessTeamUserDTO | undefined
    >();
    const accessTeamUserInit = useSearchAccessTeamUserApi();
    const saveAccessTeamUser = useSaveAccessTeamUserApi();
    const deleteAccessTeamUser = useDeleteAccessTeamUserApi();

    const { result, error, busy, initialized, initializing } = accessTeamUserInit;

    const accessTeamTitle = useCallback(
        (serial?: number) => {
            var accessTeam = result?.accessTeams.find(x => x.serial === serial);
            return <span>{accessTeam ? `${accessTeam.title} ` : serial}</span>;
        },
        [result?.accessTeams]
    );

    const userTitle = useCallback(
        (userName?: string) => {
            var user = result?.users.find(x => x.userName === userName);
            return <span>{user ? `${user.firstName} ${user.lastName} ` : userName}</span>;
        },
        [result?.users]
    );

    const columns: IBasicTableColumn<AccessTeamUserDTO>[] = useMemo(
        () => [
            {
                Header: <T>access-team-serial</T>,
                name: 'accessTeamSerial',
                width: 80,
                Cell: ({ row }) => accessTeamTitle(row.accessTeamSerial),
            },
            {
                Header: <T>user-name</T>,
                name: 'userName',
                width: 80,
                Cell: ({ row }) => userTitle(row.userName),
            },
        ],
        [accessTeamTitle, userTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        accessTeamUserInit.post(null, {});
    }, [initialized, initializing, error, accessTeamUserInit]);

    const handleDelete = (accessTeamUser: AccessTeamUserDTO) => {
        bd.msgbox(
            translate('delete-access-team-user'),
            translate('confirm-delete-access-team-user'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteAccessTeamUser.remove(
                            accessTeamUser,
                            () => {
                                accessTeamUserInit.updateResult({
                                    ...result,
                                    accessTeamUsers: result.accessTeamUsers.filter(
                                        b =>
                                            b.accessTeamSerial !==
                                                accessTeamUser.accessTeamSerial ||
                                            b.userName !== accessTeamUser.userName
                                    ),
                                });
                                notify.dark(
                                    translate('selected-access-team-user-deleted-successfully')
                                );
                                hide();
                                setShowModal(false);
                                setSelectedAccessTeamUser(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (accessTeamUser: AccessTeamUserDTO) => {
        saveAccessTeamUser.post(
            null,
            accessTeamUser,
            res => {
                if (selectedAccessTeamUser) {
                    accessTeamUserInit.updateResult({
                        ...result,
                        accessTeamUsers: result.accessTeamUsers.map(b =>
                            b.accessTeamSerial === accessTeamUser.accessTeamSerial &&
                            b.userName === accessTeamUser.userName
                                ? res.accessTeamUser
                                : b
                        ),
                    });
                } else {
                    accessTeamUserInit.updateResult({
                        ...result,
                        accessTeamUsers: [res.accessTeamUser, ...result.accessTeamUsers],
                    });
                }

                setShowModal(false);
                setSelectedAccessTeamUser(undefined);
                notify.success(
                    selectedAccessTeamUser
                        ? translate('selected-access-team-user-updated-successfully')
                        : translate('new-access-team-user-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => accessTeamUserInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="access-team-user"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-access-team-user</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedAccessTeamUser(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.accessTeamUsers}
                        onRowClick={({ row }) => {
                            setSelectedAccessTeamUser(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveAccessTeamUserModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedAccessTeamUser(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedAccessTeamUser={selectedAccessTeamUser}
                        accessTeams={result?.accessTeams}
                        users={result?.users}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
